/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment } from "react"
import { Tabs } from "@duxton-web/ui"
import { useHistory, useParams } from "react-router-dom"
import { intersection } from "lodash"
import { NonEditableMenuAlert } from "pages/menu/detail/NonEditableMenuAlert"
import { useTranslationsMap } from "common/utils/i18n"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { MenuOverviewContent } from "pages/menu/detail/menuOverview/MenuOverview"
import { ItemList } from "pages/menu/detail/items/ItemList"
import { ModifierGroups } from "pages/menu/detail/modifierGroups/ModifierGroups"
import { BulkUpload } from "pages/menu/detail/bulkUpload/BulkUpload"
import { fullPathWithSearchParams, SearchParamPairs } from "common/utils/uri"
import {
  MenuEditorTabKey,
  menuEditorTabKeys,
  menuTabEventActions,
  menuTabEventCategories,
} from "constants/menu"
import { TroyWebTracker } from "utils/tracking"
import useInventoryContext from "hooks/useInventoryContext"
import { useMenuEditorStatus } from "pages/menu/detail/hooks/useMenuEditorStatus"
import { BULK_UPLOAD_TYPE } from "pages/menu/detail/bulkUpload/BulkUploadContext"
import BulkUploadTooltip from "pages/menu/detail/bulkUpload/BulkUploadTooltip"
import BulkUploadNotifications from "pages/menu/detail/bulkUpload/bulkUploadNotifications/BulkUploadNotifications"
import { UploadType } from "types/delv-platform-api/bulkUploadTask"

const TabPane = Tabs.TabPane
interface MenuEditorSwitchProps {
  tab: MenuEditorTabKey
  routePrefix?: string
  hasAction?: boolean
  activeTab: MenuEditorTabKey
}

const BULK_UPLOAD_TABS: MenuEditorTabKey[] = [
  menuEditorTabKeys.bulkUploadInventory,
  menuEditorTabKeys.bulkUploadMenu,
]

function MenuEditorSwitch({
  tab,
  routePrefix,
  hasAction,
  activeTab,
}: MenuEditorSwitchProps) {
  switch (tab) {
    case menuEditorTabKeys.menuOverview:
      return (
        <MenuOverviewContent hasAction={hasAction} routePrefix={routePrefix} />
      )
    case menuEditorTabKeys.items:
      return <ItemList routePrefix={routePrefix} />
    case menuEditorTabKeys.modifierGroups:
      return <ModifierGroups routePrefix={routePrefix} />
    case menuEditorTabKeys.bulkUploadInventory:
      return activeTab === menuEditorTabKeys.bulkUploadInventory ? (
        <BulkUpload bulkUploadType={BULK_UPLOAD_TYPE.INVENTORY} />
      ) : null
    case menuEditorTabKeys.bulkUploadMenu:
      return activeTab === menuEditorTabKeys.bulkUploadMenu ? (
        <BulkUpload bulkUploadType={BULK_UPLOAD_TYPE.MENU} />
      ) : null
    default:
      return null
  }
}

interface MenuEditorContentProps {
  hasAction?: boolean
  routePrefix?: string
  shownTabs?: MenuEditorTabKey[]
  hideContent?: boolean
}

export const defaultShownMenuEditorTabs: MenuEditorTabKey[] = [
  menuEditorTabKeys.menuOverview,
  menuEditorTabKeys.items,
  menuEditorTabKeys.modifierGroups,
]

export const MenuEditorContent: FC<MenuEditorContentProps> = ({
  hasAction = true,
  routePrefix,
  shownTabs = defaultShownMenuEditorTabs,
  hideContent,
}: MenuEditorContentProps) => {
  const { tabKey, menuParentID } = useParams<{
    tabKey?: string
    menuParentID?: string
  }>()

  const { isEditAllowed, isEditorStatusLoading } = useMenuEditorStatus()
  const {
    isFetchingMenu,
    isMartStore,
    isMenuGroup,
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)
  const textMap = useTranslationsMap({
    menuOverview: !isMartStore
      ? "menu.tabs.overview"
      : "catalogue.tabs.overview",
    items: "menu.tabs.items",
    modifierGroups: "menu.tabs.modifierGroups",
    bulkUploadInventory: "menu.tabs.bulkUpload",
    bulkUploadMenu: "menu.tabs.bulkUpload",
  })
  const history = useHistory()
  const { isInventory } = useInventoryContext()
  const isMenuGroupPage = isMenuGroup && !isInventory
  const showNonEditableAlert =
    !isFetchingMenu && !isEditorStatusLoading && !isEditAllowed && !isInventory

  // Determine upload type for the banner
  const getUploadType = (): UploadType => {
    if (isInventory) {
      return UploadType.UpdateItemStock
    }

    return isMenuGroup
      ? UploadType.UpdateMenuGroup
      : UploadType.UpdateMenuSingle
  }
  const getBulkUploadType = (): BULK_UPLOAD_TYPE => {
    if (isInventory) {
      return BULK_UPLOAD_TYPE.INVENTORY
    }
    return BULK_UPLOAD_TYPE.MENU
  }

  const activeTabKey =
    tabKey && shownTabs.includes(tabKey as MenuEditorTabKey)
      ? tabKey
      : shownTabs[0]

  const renderTab = (tab: MenuEditorTabKey) => {
    if (tab === "bulkUploadMenu") {
      return <BulkUploadTooltip tabTitle={textMap[tab]} />
    }

    return textMap[tab]
  }

  const handleTabChange = (newTabKey: string) => {
    const searchParamPairs: SearchParamPairs = []
    const category = menuTabEventCategories[activeTabKey]
    let nextPath = `${routePrefix}/${menuParentID}`
    let action = menuTabEventActions[menuEditorTabKeys.menuOverview]
    if (shownTabs.includes(newTabKey as any)) {
      nextPath = `${nextPath}/${newTabKey}`
      action = menuTabEventActions[newTabKey]
    }

    TroyWebTracker.sendEvent({
      category,
      action,
      labels: isMenuGroupPage
        ? {
            MERCHANT_GROUP_ID: merchantGroupID,
            MENU_GROUP_ID: menuParentID,
          }
        : {
            STORE_ID: menuParentID,
          },
    })

    const destination = fullPathWithSearchParams({
      nextPath,
      searchParamPairs,
    })
    history.replace(destination)
  }

  const showBulkUploadProcessingBanner = !!intersection(
    shownTabs,
    BULK_UPLOAD_TABS,
  ).length

  return (
    <Fragment>
      {showBulkUploadProcessingBanner && !hideContent && (
        <BulkUploadNotifications
          uploadType={getUploadType()}
          bulkUploadType={getBulkUploadType()}
        />
      )}
      <div
        css={css`
          display: flex;
          height: 100%;
        `}
      >
        <div
          css={css`
            display: flex;
            flex: 1 0 auto;
            flex-flow: column;
            width: 100%;
          `}
        >
          {showNonEditableAlert && <NonEditableMenuAlert />}
          <div
            css={css`
              margin-top: 16px;
              flex-grow: 1;
            `}
          >
            <Tabs
              data-testid="menu-editor-content-tabs"
              activeKey={activeTabKey}
              defaultActiveKey="1"
              size="large"
              animated={false}
              css={css`
                height: 100%;
                overflow: hidden;
                & > .dui-tabs-content-holder {
                  display: flex;

                  & > .dui-tabs-content {
                    display: flex;

                    .dui-tabs-tabpane {
                      flex-grow: 1;
                      width: 100%;
                    }
                  }
                }
              `}
              onChange={handleTabChange}
              tabBarStyle={{
                textTransform: "uppercase",
              }}
            >
              {shownTabs.map(tab => (
                <TabPane
                  tab={renderTab(tab)}
                  key={tab}
                  css={css`
                    height: 100%;
                  `}
                >
                  {!hideContent && (
                    <MenuEditorSwitch
                      data-testid="menu-editor-switch"
                      hasAction={hasAction}
                      routePrefix={routePrefix}
                      tab={tab}
                      activeTab={activeTabKey as MenuEditorTabKey}
                    />
                  )}
                </TabPane>
              ))}
            </Tabs>
          </div>
        </div>
      </div>
    </Fragment>
  )
}
